import React, { useContext, useEffect, useState } from 'react';
import { Box, Button, Snackbar, Alert, Tooltip, CircularProgress } from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import { supabase } from '../supabaseClient';
import { AuthContext, MyContext } from '../context/AuthContext';
import { SPARK_DAILY_CAP } from '../lib/sparks';
import { hapticSuccess, hapticWarning, hapticError } from '../lib/haptics';
import { playSparkFire, playError } from '../lib/sounds';

/**
 * Fires a Spark — a zero-wallet-cost, 5-minute-lifespan burst post. Reads
 * current daily budget from the `my_spark_budget` RPC and updates it
 * locally after each successful spark. Disabled when:
 *  - user not signed in
 *  - value empty / > 280 chars
 *  - daily cap hit
 *
 * Pass `parentId` to chain a spark onto another spark — the parent
 * gets a +5 minute lifespan bonus (capped at +30 min total chain).
 */
function SparkButton({
  value,
  onFired,
  parentId,
}: {
  value: string;
  onFired?: () => void;
  parentId?: string;
}) {
  const userRecord = useContext<MyContext>(AuthContext).userRecord;
  const [remaining, setRemaining] = useState<number | null>(null);
  const [firing, setFiring] = useState(false);
  const [toast, setToast] = useState<{ severity: 'success' | 'error'; msg: string } | null>(null);

  const refreshBudget = () => {
    supabase.rpc('my_spark_budget').then(({ data, error }) => {
      if (error || !data) return;
      const row = Array.isArray(data) ? data[0] : data;
      if (row && typeof row.remaining === 'number') setRemaining(row.remaining);
    });
  };

  useEffect(() => {
    if (!userRecord) return;
    refreshBudget();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRecord?.id]);

  if (!userRecord) return null;

  const empty = !value.trim();
  const overLimit = value.length > 280;
  const capped = remaining !== null && remaining <= 0;
  const disabled = empty || overLimit || capped || firing;

  const handleFire = async () => {
    if (disabled) return;
    setFiring(true);
    try {
      const { error } = await supabase.rpc('create_spark', {
        p_value: value,
        p_parent_id: parentId ?? null,
      });
      if (error) {
        const isCap = error.message.includes('cap reached');
        setToast({ severity: 'error', msg: isCap
          ? `⚡ Daily spark cap hit (${SPARK_DAILY_CAP}/day) — try again tomorrow`
          : error.message });
        if (isCap) hapticWarning();
        else hapticError();
        return;
      }
      setRemaining((r) => (r === null ? null : Math.max(0, r - 1)));
      setToast({
        severity: 'success',
        msg: parentId
          ? '🔗 Chain reply — both sparks +5 min'
          : '⚡ Spark fired — lives 5 minutes',
      });
      hapticSuccess();
      playSparkFire();
      onFired?.();
    } catch (e: any) {
      setToast({ severity: 'error', msg: e.message ?? 'Failed to fire spark' });
      hapticError();
      playError();
    } finally {
      setFiring(false);
    }
  };

  const label = parentId
    ? (remaining !== null ? `CHAIN · ${remaining}` : 'CHAIN')
    : (remaining !== null ? `SPARK · ${remaining}` : 'SPARK');

  return (
    <>
      <Tooltip
        title={
          capped
            ? `Daily cap reached (${SPARK_DAILY_CAP}/day) — resets rolling`
            : `Fire a zero-cost, 5-minute burst post. ${remaining ?? '?'} left today.`
        }
      >
        <Box sx={{ display: 'inline-flex' }}>
          <Button
            size="small"
            variant="outlined"
            disabled={disabled}
            onClick={handleFire}
            startIcon={
              firing ? <CircularProgress size={14} sx={{ color: '#ff9800' }} /> : <BoltIcon sx={{ fontSize: 16 }} />
            }
            sx={{
              fontFamily: '"VT323", monospace',
              letterSpacing: 1.5,
              fontSize: '0.8rem',
              py: 0.3,
              px: 1.25,
              minWidth: 0,
              borderColor: capped ? 'divider' : '#ff9800',
              color: capped ? 'text.disabled' : '#ff9800',
              bgcolor: 'transparent',
              '&:hover': {
                bgcolor: 'rgba(255,152,0,0.08)',
                borderColor: '#f57c00',
              },
              '&.Mui-disabled': {
                borderColor: 'divider',
                color: 'text.disabled',
              },
            }}
          >
            {label}
          </Button>
        </Box>
      </Tooltip>

      <Snackbar
        open={!!toast}
        autoHideDuration={3500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert
            severity={toast.severity}
            onClose={() => setToast(null)}
            sx={{ width: '100%' }}
          >
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
}

export { SparkButton };
export default SparkButton;
